import { AppColors, whiteAlpha } from '@/config/appColors';
import type { AppIconName } from '@/config/appIcons';
import { formatCssColor, parseCssColor } from '@/utils/extensions/colorExtension';

/**
 * Перенос `enum ChatActionType` из `chat_page/chat_actions.dart`.
 *
 * В Dart у перечисления были поля (`buttonSize`, `iconSize`, `icon`) и
 * геттер `backgroundColor`. Здесь значением служит имя варианта, а всё
 * остальное лежит в таблице [CHAT_ACTION_TYPE_SPEC] — так `switch` по типу
 * остаётся исчерпывающим, а данные не размазаны по месту вызова.
 */

export const ChatActionType = {
  selectLanguage: 'selectLanguage',
  keyboardType: 'keyboardType',
  closeConnection: 'closeConnection',
  backButton: 'backButton',
} as const;

export type ChatActionType = (typeof ChatActionType)[keyof typeof ChatActionType];

/** `Colors.white.withValues(alpha: 0.3)` — фон трёх «белых» кнопок. */
const NEUTRAL_BACKGROUND_COLOR = whiteAlpha(0.3);

/**
 * `AppColors.cE94040.withValues(alpha: 0.5)`.
 *
 * Тот же красный фон стоит у кнопки отмены в клавиатурном режиме
 * (`chat_keyboard_mode.dart`), поэтому цвет объявлен один раз здесь.
 */
export const CANCEL_BACKGROUND_COLOR = formatCssColor({
  ...parseCssColor(AppColors.cE94040),
  alpha: 0.5,
});

/** Поля варианта перечисления `ChatActionType`. */
export interface ChatActionTypeSpec {
  /** Значение из Dart, до умножения на `.w`. */
  readonly buttonSize: number;
  readonly iconSize: number;
  readonly icon: AppIconName;
  readonly backgroundColor: string;
}

export const CHAT_ACTION_TYPE_SPEC: Readonly<
  Record<ChatActionType, ChatActionTypeSpec>
> = {
  [ChatActionType.selectLanguage]: {
    buttonSize: 80,
    iconSize: 40,
    icon: 'translate',
    backgroundColor: NEUTRAL_BACKGROUND_COLOR,
  },
  [ChatActionType.keyboardType]: {
    buttonSize: 80,
    iconSize: 40,
    icon: 'keyboard',
    backgroundColor: NEUTRAL_BACKGROUND_COLOR,
  },
  [ChatActionType.closeConnection]: {
    buttonSize: 70,
    iconSize: 30,
    icon: 'cancel',
    backgroundColor: CANCEL_BACKGROUND_COLOR,
  },
  [ChatActionType.backButton]: {
    buttonSize: 80,
    iconSize: 40,
    icon: 'arrowLeft',
    backgroundColor: NEUTRAL_BACKGROUND_COLOR,
  },
};
